/**
 * SubscriptionLifecycleBanner — Phase 5 Dashboard banner.
 *
 * Renders the single most-urgent lifecycle state from a priority queue:
 *
 *   payment-failed > expired > expiring-soon (≤3d) > grandfather-end (≤7d) > expiring-this-week (4-7d)
 *
 * Why mutually exclusive: stacking three red banners on top of each
 * other trains users to dismiss them all without reading. One banner,
 * one CTA, the most urgent thing first.
 *
 * Below the priority banner we ALSO render an amber "N pages paused"
 * notice when overCapWarning.pages.paused > 0 — that one is a state
 * report, not a renewal nudge, so it can coexist.
 *
 * Refresh policy mirrors usePlanUsage.ts:
 *   - on mount
 *   - every 5 minutes
 *   - on window focus (so a renewal in another tab clears the banner
 *     immediately, instead of waiting up to 5 min for the next poll)
 *
 * Dismissal policy: re-dismissed daily, not forever. localStorage key
 * is `lifecycle-banner-dismissed:${type}:${YYYYMMDD}` so a fresh banner
 * the next morning bypasses yesterday's dismissal. Critical states
 * (≤1d, expired, payment-failed) are NOT dismissible — those represent
 * either money-on-the-table (failed retry) or active feature lockout,
 * both of which need a continuous nudge.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  CreditCard,
  CalendarClock,
  ArrowRight,
  Loader2,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const REFRESH_INTERVAL_MS = 5 * 60_000;

type LifecycleStatus = {
  plan: 'free' | 'pro' | 'agency' | 'enterprise';
  subscriptionExpiresAt: string | null;
  grandfatheredUntil: string | null;
  daysUntilExpiry: number | null;
  daysUntilGrandfatherEnd: number | null;
  inGracePeriod: boolean;
  overCapWarning: {
    pages?: { used: number; max: number; paused: number };
    customDomains?: { used: number; max: number };
  } | null;
  lastFailedPaymentAt: string | null;
};

type BannerKind =
  | 'paymentFailed'
  | 'expired'
  | 'expiringIn1'
  | 'expiringIn3'
  | 'expiringIn7'
  | 'grandfatherIn1'
  | 'grandfatherIn3'
  | 'grandfatherIn7';

type Severity = 'critical' | 'warn' | 'info';

type CtaAction = 'renew' | 'navigate';

interface PriorityBanner {
  kind: BannerKind;
  severity: Severity;
  titleKey: string;
  bodyKey?: string;
  ctaKey: string;
  ctaPath: string;
  /** renew → create open invoice for current plan, then /payment-history */
  ctaAction?: CtaAction;
  vars?: Record<string, string | number>;
  dismissible: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

function authHeader(): HeadersInit {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function todayBucket(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function dismissalKey(kind: BannerKind): string {
  return `lifecycle-banner-dismissed:${kind}:${todayBucket()}`;
}

function formatDate(iso: string | null, lang: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function planLabel(plan: LifecycleStatus['plan']): string {
  if (plan === 'free') return 'Free';
  if (plan === 'pro') return 'Pro';
  if (plan === 'agency') return 'Agency';
  return 'Enterprise';
}

/**
 * Resolve the single highest-priority banner the user should see.
 * Returns null when nothing matches (happy state). Order matters —
 * the first match wins.
 */
function resolvePriorityBanner(
  status: LifecycleStatus,
  lang: string
): PriorityBanner | null {
  const planName = planLabel(status.plan);
  const expDate = formatDate(status.subscriptionExpiresAt, lang);
  const grandfatherDate = formatDate(status.grandfatheredUntil, lang);

  // 1) Payment failed in the last 30 days — top priority because it's
  //    a recoverable revenue event.
  if (status.lastFailedPaymentAt) {
    return {
      kind: 'paymentFailed',
      severity: 'critical',
      titleKey: 'lifecycleBanner.paymentFailed',
      ctaKey: 'lifecycleBanner.cta.retry',
      ctaPath: '/upgrade',
      ctaAction: 'navigate',
      vars: { plan: planName },
      dismissible: false,
      icon: CreditCard,
    };
  }

  const days = status.daysUntilExpiry;

  // 2) Expired (today or up to 7 days ago — the grace window).
  if (days !== null && days < 0) {
    if (days === 0 || days === -0) {
      return {
        kind: 'expired',
        severity: 'critical',
        titleKey: 'lifecycleBanner.expiredToday',
        ctaKey: 'lifecycleBanner.cta.renew',
        ctaPath: '/payment-history',
        ctaAction: 'renew',
        dismissible: false,
        icon: AlertCircle,
      };
    }
    return {
      kind: 'expired',
      severity: 'critical',
      titleKey: 'lifecycleBanner.expiredDaysAgo',
      ctaKey: 'lifecycleBanner.cta.renew',
      ctaPath: '/payment-history',
      ctaAction: 'renew',
      vars: { days: Math.abs(days) },
      dismissible: false,
      icon: AlertCircle,
    };
  }

  // 3) Expiring tomorrow or today — critical, non-dismissible.
  if (days !== null && days <= 1 && days >= 0) {
    return {
      kind: 'expiringIn1',
      severity: 'critical',
      titleKey: 'lifecycleBanner.expiringIn1',
      ctaKey: 'lifecycleBanner.cta.renew',
      ctaPath: '/payment-history',
      ctaAction: 'renew',
      vars: { plan: planName, date: expDate },
      dismissible: false,
      icon: AlertTriangle,
    };
  }

  // 4) Expiring in 2-3 days — warn, dismissible (daily reset).
  if (days !== null && days <= 3 && days > 1) {
    return {
      kind: 'expiringIn3',
      severity: 'warn',
      titleKey: 'lifecycleBanner.expiringIn3',
      ctaKey: 'lifecycleBanner.cta.renew',
      ctaPath: '/payment-history',
      ctaAction: 'renew',
      vars: { plan: planName },
      dismissible: true,
      icon: AlertTriangle,
    };
  }

  // 5) Grandfather end ≤ 7d — between expiring-soon and expiring-this-week.
  //    Highest grandfather urgency tier escalates first.
  const gDays = status.daysUntilGrandfatherEnd;
  if (gDays !== null && gDays >= 0 && gDays <= 7) {
    if (gDays <= 1) {
      return {
        kind: 'grandfatherIn1',
        severity: 'warn',
        titleKey: 'lifecycleBanner.grandfatherIn1',
        ctaKey: 'lifecycleBanner.cta.renew',
        ctaPath: '/payment-history',
        ctaAction: 'renew',
        vars: { date: grandfatherDate },
        dismissible: true,
        icon: CalendarClock,
      };
    }
    if (gDays <= 3) {
      return {
        kind: 'grandfatherIn3',
        severity: 'warn',
        titleKey: 'lifecycleBanner.grandfatherIn3',
        ctaKey: 'lifecycleBanner.cta.renew',
        ctaPath: '/payment-history',
        ctaAction: 'renew',
        vars: { date: grandfatherDate },
        dismissible: true,
        icon: CalendarClock,
      };
    }
    return {
      kind: 'grandfatherIn7',
      severity: 'info',
      titleKey: 'lifecycleBanner.grandfatherIn7',
      ctaKey: 'lifecycleBanner.cta.renew',
      ctaPath: '/payment-history',
      ctaAction: 'renew',
      vars: { date: grandfatherDate },
      dismissible: true,
      icon: CalendarClock,
    };
  }

  // 6) Expiring this week (4-7d) — info, dismissible.
  if (days !== null && days >= 4 && days <= 7) {
    return {
      kind: 'expiringIn7',
      severity: 'info',
      titleKey: 'lifecycleBanner.expiringIn7',
      ctaKey: 'lifecycleBanner.cta.renew',
      ctaPath: '/payment-history',
      ctaAction: 'renew',
      vars: { plan: planName, date: expDate },
      dismissible: true,
      icon: Info,
    };
  }

  return null;
}

const SEVERITY_STYLES: Record<
  Severity,
  { wrap: string; iconColor: string; cta: string; text: string }
> = {
  critical: {
    wrap: 'bg-red-50 border-red-300',
    iconColor: 'text-red-500',
    cta: 'bg-red-600 hover:bg-red-700 text-white',
    text: 'text-red-900',
  },
  warn: {
    wrap: 'bg-amber-50 border-amber-300',
    iconColor: 'text-amber-500',
    cta: 'bg-amber-600 hover:bg-amber-700 text-white',
    text: 'text-amber-900',
  },
  info: {
    wrap: 'bg-blue-50 border-blue-300',
    iconColor: 'text-blue-500',
    cta: 'bg-blue-600 hover:bg-blue-700 text-white',
    text: 'text-blue-900',
  },
};

export const SubscriptionLifecycleBanner: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const [status, setStatus] = useState<LifecycleStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissedKind, setDismissedKind] = useState<BannerKind | null>(null);
  const isMountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    const headers = authHeader();
    if (!('Authorization' in headers)) {
      if (isMountedRef.current) setLoaded(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/lifecycle/status`, { headers });
      if (!res.ok) {
        if (isMountedRef.current) setLoaded(true);
        return;
      }
      const json = (await res.json()) as LifecycleStatus;
      if (!isMountedRef.current) return;
      setStatus(json);
      setLoaded(true);
    } catch (err) {
      console.warn('[lifecycleBanner] fetch failed', err);
      if (isMountedRef.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchStatus();
    const id = window.setInterval(fetchStatus, REFRESH_INTERVAL_MS);
    const onFocus = () => {
      fetchStatus();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      isMountedRef.current = false;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchStatus]);

  const banner = status ? resolvePriorityBanner(status, i18n.language) : null;

  // Hydrate the dismissal flag whenever the resolved banner changes
  // (different days surface different priority kinds; we don't want
  // yesterday's dismissal to silently kill today's banner of the same
  // type — the date bucket inside the storage key handles that).
  useEffect(() => {
    if (!banner) {
      setDismissedKind(null);
      return;
    }
    const key = dismissalKey(banner.kind);
    if (window.localStorage.getItem(key) === '1') {
      setDismissedKind(banner.kind);
    } else {
      setDismissedKind(null);
    }
  }, [banner?.kind]);

  const [renewBusy, setRenewBusy] = useState(false);

  const handleDismiss = useCallback(() => {
    if (!banner || !banner.dismissible) return;
    window.localStorage.setItem(dismissalKey(banner.kind), '1');
    setDismissedKind(banner.kind);
  }, [banner]);

  const createRenewalInvoice = useCallback(async () => {
    const headers = authHeader();
    if (!('Authorization' in headers)) {
      navigate('/login');
      return;
    }
    setRenewBusy(true);
    try {
      const res = await fetch(`${API_URL}/billing/intents/renew`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 409 && data.code === 'OPEN_INVOICE_EXISTS') {
        toast.error(
          t('lifecycleBanner.toast.openInvoiceExists', {
            orderNo: data.orderNo || '',
            defaultValue:
              'คุณมีใบแจ้งหนี้ค้างชำระอยู่แล้ว ({{orderNo}}) — ไปชำระหรือยกเลิกก่อน',
          }),
        );
        navigate('/payment-history');
        return;
      }

      if (!res.ok) {
        throw new Error(
          data.error ||
            t('lifecycleBanner.toast.renewFailed', {
              defaultValue: 'สร้างใบแจ้งหนี้ไม่สำเร็จ กรุณาลองใหม่',
            }),
        );
      }

      toast.success(
        t('lifecycleBanner.toast.renewCreated', {
          defaultValue: 'สร้างใบแจ้งหนี้ต่ออายุแล้ว — กรุณาชำระเงิน',
        }),
      );
      navigate('/payment-history');
    } catch (err) {
      console.error('[lifecycleBanner] renew failed', err);
      const message =
        err instanceof Error
          ? err.message
          : t('lifecycleBanner.toast.renewFailed', {
              defaultValue: 'สร้างใบแจ้งหนี้ไม่สำเร็จ กรุณาลองใหม่',
            });
      toast.error(message);
    } finally {
      setRenewBusy(false);
    }
  }, [navigate, t]);

  const handleCta = useCallback(
    (bannerCta: PriorityBanner) => {
      if (bannerCta.ctaAction === 'renew') {
        void createRenewalInvoice();
        return;
      }
      navigate(bannerCta.ctaPath);
    },
    [createRenewalInvoice, navigate]
  );

  if (!loaded || !status) return null;

  const showPriorityBanner = banner && dismissedKind !== banner.kind;
  const pausedCount = status.overCapWarning?.pages?.paused ?? 0;
  const showPagesPausedNotice = pausedCount > 0;

  if (!showPriorityBanner && !showPagesPausedNotice) return null;

  return (
    <div className="mb-6 space-y-3">
      {showPriorityBanner && banner && (
        <BannerCard
          banner={banner}
          onDismiss={handleDismiss}
          onCta={handleCta}
          busy={renewBusy}
          t={t}
          dismissLabel={t('actions.dismiss')}
        />
      )}

      {showPagesPausedNotice && (
        <PagesPausedNotice
          count={pausedCount}
          onCta={() => navigate('/dashboard')}
          t={t}
        />
      )}
    </div>
  );
};

interface BannerCardProps {
  banner: PriorityBanner;
  onDismiss: () => void;
  onCta: (banner: PriorityBanner) => void;
  busy?: boolean;
  t: ReturnType<typeof useTranslation>['t'];
  dismissLabel: string;
}

const BannerCard: React.FC<BannerCardProps> = ({
  banner,
  onDismiss,
  onCta,
  busy = false,
  t,
  dismissLabel,
}) => {
  const styles = SEVERITY_STYLES[banner.severity];
  const Icon = banner.icon;
  return (
    <div
      className={`relative border rounded-lg p-4 shadow-sm ${styles.wrap}`}
      role="alert"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${styles.iconColor}`} />
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${styles.text}`}>
              {t(banner.titleKey, banner.vars)}
            </p>
            {banner.bodyKey && (
              <p className={`text-sm mt-1 ${styles.text} opacity-80`}>
                {t(banner.bodyKey, banner.vars)}
              </p>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => onCta(banner)}
              className={`mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${styles.cta}`}
            >
              {busy ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t('lifecycleBanner.cta.renewing', {
                    defaultValue: 'กำลังสร้างใบแจ้งหนี้…',
                  })}
                </>
              ) : (
                <>
                  {t(banner.ctaKey)}
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
        {banner.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            className={`shrink-0 p-1 rounded transition-opacity hover:opacity-70 ${styles.text}`}
            aria-label={dismissLabel}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

interface PagesPausedNoticeProps {
  count: number;
  onCta: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

const PagesPausedNotice: React.FC<PagesPausedNoticeProps> = ({
  count,
  onCta,
  t,
}) => {
  return (
    <div
      className="relative border rounded-lg p-4 shadow-sm bg-amber-50 border-amber-300"
      role="status"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900">
              {t('lifecycleBanner.pagesPaused', { count })}
            </p>
            <button
              type="button"
              onClick={onCta}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white transition-colors"
            >
              {t('lifecycleBanner.cta.managePages')}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
